import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useAlerts from './useAlerts';
import AlertsPanel from './AlertsPanel';
import { alertsBellCSS } from './AlertsBell.css';

const RULE_LABELS = {
    registro_sin_avance: 'Registro sin avance',
    buro_exitoso_sin_documentos: 'Buró exitoso sin documentos',
    documentos_incompletos_24h: 'Documentos incompletos hace 24h',
};

const RULE_ICONS = {
    registro_sin_avance: 'pi pi-user',
    buro_exitoso_sin_documentos: 'pi pi-id-card',
    documentos_incompletos_24h: 'pi pi-file',
};

const STATUS_LABELS = {
    open: 'Abierta',
    silenced: 'Silenciada',
    ignored: 'Ignorada',
    resolved: 'Resuelta',
};

const SEVERITY_MAP = {
    critical: { bg: '#fee2e2', fg: '#991b1b', border: '#ef4444' },
    error:    { bg: '#fee2e2', fg: '#991b1b', border: '#ef4444' },
    high:     { bg: '#ffedd5', fg: '#9a3412', border: '#f97316' },
    warning:  { bg: '#fef3c7', fg: '#92400e', border: '#f59e0b' },
    medium:   { bg: '#fef3c7', fg: '#92400e', border: '#f59e0b' },
    low:      { bg: '#dbeafe', fg: '#1e40af', border: '#3b82f6' },
    info:     { bg: '#e0f2fe', fg: '#075985', border: '#34C6F1' },
};

function timeAgo(iso) {
    if (!iso) return '—';
    const diffSec = (Date.now() - new Date(iso).getTime()) / 1000;
    if (Number.isNaN(diffSec) || diffSec < 0) return '—';
    if (diffSec < 60) return 'hace unos seg.';
    if (diffSec < 3600) return `hace ${Math.floor(diffSec / 60)} min`;
    if (diffSec < 86400) return `hace ${Math.floor(diffSec / 3600)} h`;
    return `hace ${Math.floor(diffSec / 86400)} d`;
}

function sevFor(severity) {
    const key = String(severity || 'info').toLowerCase();
    return SEVERITY_MAP[key] || SEVERITY_MAP.info;
}

export default function AlertsBell() {
    const {
        alerts,
        unreadCount,
        loading,
        pending,
        markSeen,
        ignore,
        silence,
        resolve,
        refetch,
    } = useAlerts();

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [panelOpen, setPanelOpen] = useState(false);
    const wrapperRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('alerts') === 'open') {
            setPanelOpen(true);
            params.delete('alerts');
            const query = params.toString();
            navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true });
        }
    }, [location.search, location.pathname, navigate]);

    useEffect(() => {
        if (!dropdownOpen) return;
        const onClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [dropdownOpen]);

    // Trae la lista completa SOLO al abrir la campana o el panel (no en segundo plano).
    useEffect(() => {
        if (dropdownOpen || panelOpen) refetch();
    }, [dropdownOpen, panelOpen, refetch]);

    const top5 = (alerts || []).slice(0, 5);
    const stop = (e) => e.stopPropagation();

    return (
        <div css={alertsBellCSS()} ref={wrapperRef}>
            <button
                type="button"
                className={`ab-trigger ${dropdownOpen ? 'active' : ''} ${unreadCount > 0 ? 'has-alerts' : ''}`}
                onClick={() => setDropdownOpen((v) => !v)}
                aria-label={`Alertas${unreadCount > 0 ? ` (${unreadCount} sin leer)` : ''}`}
                aria-haspopup="true"
                aria-expanded={dropdownOpen}
                title="Alertas"
            >
                <i className="pi pi-bell" />
                {unreadCount > 0 && (
                    <span className="ab-badge">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {dropdownOpen && (
                <div className="ab-dropdown" role="menu">
                    <div className="ab-header">
                        <div className="ab-header-title">
                            <span className="ico"><i className="pi pi-bell" /></span>
                            <strong>Alertas</strong>
                        </div>
                        <div className="ab-header-actions">
                            <button
                                type="button"
                                className="ab-icon-btn"
                                onClick={() => refetch()}
                                disabled={loading}
                                title="Actualizar"
                                aria-label="Actualizar"
                            >
                                <i className={`pi pi-refresh ${loading ? 'pi-spin' : ''}`} />
                            </button>
                        </div>
                    </div>

                    <div className="ab-list">
                        {top5.length === 0 && (
                            <div className="ab-empty">
                                <div className="ab-empty-icon"><i className="pi pi-check-circle" /></div>
                                <p className="ab-empty-title">Todo bajo control</p>
                                <p className="ab-empty-sub">No hay alertas activas.</p>
                            </div>
                        )}

                        {top5.map((a) => {
                            const sev = sevFor(a.severity);
                            const isOpen = a.status === 'open';
                            const busy = pending[a.id];
                            return (
                                <div
                                    key={a.id}
                                    className={`ab-card ${a.seen ? '' : 'unseen'} ${busy ? 'busy' : ''}`}
                                    style={{
                                        '--sev-border': sev.border,
                                        '--sev-bg': sev.bg,
                                        '--sev-fg': sev.fg,
                                    }}
                                    onClick={() => !busy && markSeen(a.id)}
                                >
                                    <span className="ab-card-icon">
                                        <i className={RULE_ICONS[a.rule_code] || 'pi pi-bell'} />
                                    </span>
                                    <div className="ab-card-body">
                                        <div className="ab-card-title">{a.title}</div>
                                        <div className="ab-card-message">{a.message}</div>
                                        <div className="ab-card-meta">
                                            <span>{RULE_LABELS[a.rule_code] || a.rule_code}</span>
                                            <span className="sep" />
                                            <span>{timeAgo(a.detected_at)}</span>
                                            <span className="sep" />
                                            <span className={`status-chip ${a.status}`}>
                                                {STATUS_LABELS[a.status] || a.status}
                                            </span>
                                        </div>
                                        {isOpen && (
                                            <div className="ab-card-actions" onClick={stop}>
                                                <button
                                                    type="button"
                                                    className="ab-action-btn ignore"
                                                    title="Ignorar"
                                                    aria-label="Ignorar"
                                                    onClick={() => ignore(a.id)}
                                                    disabled={!!busy}
                                                >
                                                    <i className={busy === 'ignore' ? 'pi pi-spin pi-spinner' : 'pi pi-ban'} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="ab-action-btn silence"
                                                    title="Silenciar 24h"
                                                    aria-label="Silenciar 24h"
                                                    onClick={() => silence(a.id, 24)}
                                                    disabled={!!busy}
                                                >
                                                    <i className={busy === 'silence' ? 'pi pi-spin pi-spinner' : 'pi pi-clock'} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="ab-action-btn resolve"
                                                    title="Resolver"
                                                    aria-label="Resolver"
                                                    onClick={() => resolve(a.id)}
                                                    disabled={!!busy}
                                                >
                                                    <i className={busy === 'resolve' ? 'pi pi-spin pi-spinner' : 'pi pi-check'} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <button
                        type="button"
                        className="ab-footer-btn"
                        onClick={() => {
                            setDropdownOpen(false);
                            setPanelOpen(true);
                        }}
                    >
                        Ver todas
                        <i className="pi pi-arrow-right" style={{ fontSize: '0.7rem' }} />
                    </button>
                </div>
            )}

            <AlertsPanel
                visible={panelOpen}
                onClose={() => setPanelOpen(false)}
                alerts={alerts}
                loading={loading}
                pending={pending}
                onRefresh={refetch}
                onIgnore={ignore}
                onSilence={silence}
                onResolve={resolve}
                onMarkSeen={markSeen}
            />
        </div>
    );
}
